import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createFavouritesService,
  createApiFavouritesClient,
  createPendingFavouritesSessionStorageStore,
  isFavouritesClientAddAuthenticationError,
  isFavouritesClientRemoveAuthenticationError,
} from '@johnlewispartnership/wtr-website-favourites-sdk';
import type { Favourite } from '@johnlewispartnership/wtr-website-favourites-sdk';
import { useAuthentication } from '@johnlewispartnership/wtr-website-authentication/dist/context';

import { getFavouritesQueryErrorModal } from 'services/favourites/getFavouritesQueryErrorModal';
import { updateFavouriteMutationErrorModal } from 'services/favourites/updateFavouriteMutationErrorModal';
import config from 'config';
import FavouritesServiceClient from 'services/favourites';

const FAVOURITES_LIST_QUERY_KEY = 'wtr-cr/favourites-service/favourites-list';
const FAVOURITES_TOGGLE_MUTATION_KEY = 'wtr-cr/favourites-service/toggle-favourite';

const apiClientHeaders = {
  headers: {
    breadcrumb: config.applicationName,
  },
};

const useFavouritesClient = () => {
  const { state, redirectToLogin } = useAuthentication();
  const { customerId, token } = state;
  const isAuthenticated = !!token?.accessToken;

  const favouritesService = useMemo(() => {
    const favouritesStore = createPendingFavouritesSessionStorageStore();
    const pendingFavouritesStore = {
      stash: favouritesStore.stash,
      unstash: favouritesStore.unstash,
    };

    return createFavouritesService({
      favouritesClient: createApiFavouritesClient({
        apiClient: FavouritesServiceClient,
        apiClientOptions: {
          get: { ...apiClientHeaders },
          add: { ...apiClientHeaders },
          remove: { ...apiClientHeaders },
        },
        authenticationClient: {
          getAccessToken: () => token?.accessToken ?? '',
          isAuthenticated: () => isAuthenticated,
        },
      }),
      pendingFavouritesStore: pendingFavouritesStore,
    });
  }, [token?.accessToken, isAuthenticated]);

  return {
    favouritesService,
    isAuthenticated,
    customerId,
    redirectToLogin,
  };
};

export const useFavouritesService = () => {
  const { favouritesService, isAuthenticated, customerId, redirectToLogin } = useFavouritesClient();
  const queryClient = useQueryClient();
  const queryKey = [FAVOURITES_LIST_QUERY_KEY, customerId];
  const mutationKey = [FAVOURITES_TOGGLE_MUTATION_KEY, customerId];

  const { data: favourites = [] } = useQuery({
    meta: {
      getErrorModal: getFavouritesQueryErrorModal,
    },
    queryKey,
    queryFn: async () => {
      await favouritesService.reconcilePending();

      return await favouritesService.get();
    },
    enabled: isAuthenticated,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const favouriteLineNumbers: Record<Favourite['lineNumber'], boolean> = {};

  for (const favourite of favourites) {
    favouriteLineNumbers[favourite.lineNumber] = true;
  }

  const toggleFavourite = useMutation({
    meta: {
      getErrorModal: updateFavouriteMutationErrorModal,
    },
    mutationKey,
    mutationFn: async (lineNumber: string) => {
      const isCurrentlyFavourite = favouriteLineNumbers[lineNumber];

      if (isCurrentlyFavourite) {
        await favouritesService.remove(lineNumber);
        return;
      }

      await favouritesService.add(lineNumber);
    },
    onMutate: async (lineNumber: string) => {
      await queryClient.cancelQueries({ queryKey });

      const previousFavourites = queryClient.getQueryData<Favourite[]>(queryKey);

      if (previousFavourites?.some(favourite => favourite.lineNumber === lineNumber)) {
        queryClient.setQueryData<Favourite[]>(
          queryKey,
          previousFavourites.filter(favourite => favourite.lineNumber !== lineNumber),
        );

        return { previousFavourites, action: 'remove' as const };
      }

      queryClient.setQueryData<Favourite[]>(queryKey, currentFavourites => {
        return [
          ...(currentFavourites ?? []),
          {
            lineNumber,
            timesOrdered: 0,
            lastPurchase: null,
            userSelected: true,
            boughtOnline: false,
            boughtInBranch: false,
            timesOrderedInBranch: 0,
            lastPurchaseInBranch: null,
            created: new Date().toISOString(),
          },
        ];
      });

      return { previousFavourites, action: 'add' as const };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousFavourites);

      if (
        isFavouritesClientAddAuthenticationError(error) ||
        isFavouritesClientRemoveAuthenticationError(error)
      ) {
        redirectToLogin();
        return;
      }
    },
  });

  return {
    favourites: favouriteLineNumbers,
    toggleFavourite: (lineNumber: string) => toggleFavourite.mutate(lineNumber),
  };
};
